import { Injectable, inject } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { BalansHeaderCell, SettingsCell } from "~app/features/forsaljning/kundorder/kundorder-details/kundorderrad-details/kor-hot/kor-hot.cells";
import { CellFactory, TextCell } from "~app/features/forsaljning/leverantorsorder/leverantorsorder-details/leverantorsorder-leveransrader/lev-hot-cells";
import { SetForsorjningsradPlanLevorderCommand } from "~app/features/justera-leveransplan/common/commands/setForsorjningsradPlanCommands";
import { SetLevorderLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/setLevorderLeveransradKvantitetCommand";
import { SetOverenskommenKvantitetLevorderCommand } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { SetLeverantorsorderradKvantitetCommand } from "../../commands/SetLeverantorsorderradKvantitetCommand";
import { LeveransraderEditComponent } from "../../leveransrader-edit/leveransrader-edit.component";
import { LeverantorsorderradAggregat } from "./leverantorsorderradAggregat";

@Injectable({
    providedIn: "root"
})

/* eslint-disable no-bitwise */

export class LeveransraderHotService {
    private readonly $translate = inject(TranslateService);
    private readonly commandService = inject(CommandService);
    private readonly fordelaService = inject(FordelaService);
    private readonly modalService = inject(NgbModal);

    createHotDoc = (model: LeverantorsorderradAggregat, readOnly) => {

        const colFactory = () => this.buildColumnDefinitions(model.period);
        const rowFactory = (doc: HotDoc) => {
            doc.createRow(row => this.buildLeveransrad(row, model), Cellstyle.StrongTop);
            doc.createRow(row => this.buildTransporteratrad(row,  model));
            doc.createRow(row => this.buildBalansrad(row,  model));
            doc.createRow(row => this.buildForsorjningsraderRad(row, model));

            model.forsorjningskallor.forEach(forsorjningskalla => {
                doc.createRow(row => this.buildForsorjningsradPlanrad(row, forsorjningskalla, model, doc, readOnly), Cellstyle.Default);
                doc.createRow(row => this.buildForsorjningsradTransporteratrad(row, forsorjningskalla, model.period), Cellstyle.Default);
            });
        };
        return new HotDoc(colFactory, rowFactory);
    };

    getPeriods = (periodFrom, periodTom): IAggregatPeriod[] => {
        const defaultPeriod = {
            type: "PERIOD",
            from: periodFrom,
            to: periodTom,
            periodMonth: periodFrom
        };
        return AggregatPeriods.create("sv-SE", defaultPeriod);
    };

    private readonly buildForsorjningsradTransporteratrad = (row: HotRow, model: ForsorjningskallaAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("settings", Cellstyle.Default);
        row.addText("rowHeader4", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumTransporterat(period);
            grandtotal += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("overenskommet", "", Cellstyle.Readonly);
    };

    private readonly buildForsorjningsradPlanrad =
        (
            row: HotRow,
            model: ForsorjningskallaAggregat,
            leverantorsorderradAggregat: LeverantorsorderradAggregat,
            doc: HotDoc,
            readOnly: boolean): void => {
            if (!readOnly) {
                const settingsCell = new SettingsCell(
                    Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
                    { rowSpan: 2 },
                    () => this.onModifyForsorjningsrad(leverantorsorderradAggregat, model, doc));
                row.add("settings", settingsCell);
            }

            row.add("rowHeader1", TextCell.create(model.mottagningsplats));
            row.add("rowHeader2", CellFactory.createKundCell(model));
            row.add("rowHeader3", TextCell.create(model.sortiment));
            row.addText("rowHeader4", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_PLAN"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

            leverantorsorderradAggregat.period.forEach(period => {
                const total = model.sumPlan(period);
                const isActive = period.isWithin(leverantorsorderradAggregat.periodFrom, leverantorsorderradAggregat.periodTom);
                const style = isActive ? 0 : Cellstyle.Readonly;
                row.addNumeric(period.key, total, style, change => this.onUpdateForsorjningsradPlan(model, leverantorsorderradAggregat, period, change));
            });
            row.addNumeric("overenskommet", model.overenskommet, null,
                change => this.onUpdateOverenskommenKvantitet(model, leverantorsorderradAggregat.orderradId, change));

        };

    private readonly buildBalansrad = (row: HotRow, model: LeverantorsorderradAggregat): void => {
        row.add("rowHeader4", new BalansHeaderCell(this.$translate, 1));

        let grandtotal = 0;
        model.period.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;
            const style = Cellstyle.Readonly | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("overenskommet", null, Cellstyle.Readonly);
    };

    private readonly buildForsorjningsraderRad = (row: HotRow, model: LeverantorsorderradAggregat): void => {
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_FORSORJNINGSGRADER");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addText("rowHeader4", header, headerStyle);

        model.period.forEach(period => {
            const sumForsorjningsrader = model.sumForsorjningsrader(period);
            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;
            row.addNumeric(period.key, sumForsorjningsrader, style);
        });
    };

    private readonly buildTransporteratrad = (row: HotRow, model: LeverantorsorderradAggregat): void => {
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addText("rowHeader4", header, headerStyle);

        let grandtotal = 0;
        model.period.forEach(period => {
            const transporterat = model.sumTransporterat(period);
            grandtotal += transporterat;

            // Jämför mot kundorderradens aggregat för aktuella försörjningskällor
            const forsorjningskallor = model.forsorjningskallor.map(kalla => kalla.id);
            const transporteratForsorjningskallor = model.sumTransporteratForsorjningskallor(period, forsorjningskallor);

            const flash = transporteratForsorjningskallor !== transporterat ? Cellstyle.Flash : Cellstyle.Default;
            row.addNumeric(period.key, transporterat, Cellstyle.Readonly | flash);
        });
        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildLeveransrad = (row: HotRow, model: LeverantorsorderradAggregat): void => {
        row.addEmpty("settings", Cellstyle.Readonly); // Behövs för rad-style
        const header = this.$translate.instant("KUNDORDER_LEVRADER_HEADER_LEVERANSRAD");
        const headerStyle = Cellstyle.Readonly | Cellstyle.AlignRight;
        row.addEmpty("rowHeader1", headerStyle);
        row.addEmpty("rowHeader2", headerStyle);
        row.add("rowHeader3", TextCell.create(model.handelssortimentsgrupp.namn, 1, headerStyle | Cellstyle.StrongTop));
        row.addText("rowHeader4", header, headerStyle);

        model.period.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? Cellstyle.Default : Cellstyle.Readonly;
            row.addNumeric(period.key, sumLeveransrad, style, arg => this.onUpdateLeveransradKvantitet(arg, period, model));
        });
        row.addEmpty("summa", Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("overenskommet", model.overenskommet, Cellstyle.Default, change => this.onUpdateLeverantorsorderradKvantitet(change, model));
    };

    private readonly onModifyForsorjningsrad = (levorderAgg: LeverantorsorderradAggregat, model: ForsorjningskallaAggregat, doc: HotDoc) => {
        const modalRef = this.modalService.open(LeveransraderEditComponent, {
            keyboard: false,
            centered: true,
            backdrop: "static",
            size: "lg"
        });
        modalRef.componentInstance.leverantorsorderradAggregat = levorderAgg;
        modalRef.componentInstance.forsorjningskallaAggregat = model;
        modalRef.result.then(r => {
            if (r) {
                doc.rebind();
            }
        });
    };

    private readonly onUpdateForsorjningsradPlan = (
        model: ForsorjningskallaAggregat,
        leverantorsorderradAggregat: LeverantorsorderradAggregat,
        period: IAggregatPeriod,
        change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(leverantorsorderradAggregat.periodFrom);
        const periodEnd = period.getEndOrEarlier(leverantorsorderradAggregat.periodTom);

        this.fordelaService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradPlan(result);
                const command = new SetForsorjningsradPlanLevorderCommand(i => {
                    i.kundorderradId = model.id;
                    i.periodStart = period.periodStartDate;
                    i.periodEnd = period.periodEndDate;
                    i.leverantorsorderradId = leverantorsorderradAggregat.orderradId;
                    i.fordeladKvantitet = result;
                });
                this.commandService.addTransactionCommand(command);
                change.complete();
            });
    };

    private readonly onUpdateLeveransradKvantitet = (change: NumericCellChangeArg, period: IAggregatPeriod, model: LeverantorsorderradAggregat) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);
        this.fordelaService.getFordeladKvantitet(change.newValue, periodStart, periodEnd).subscribe(result => {
            model.updateLeveransradKvantitet(result);
            const command = new SetLevorderLeveransradKvantitetCommand(c => {
                c.leverantorsorderradId = model.orderradId;
                c.periodStart = periodStart;
                c.periodEnd = periodEnd;
                c.fordeladKvantitet = result;
            });
            this.commandService.addTransactionCommand(command);
            change.complete();
        });
    };

    private readonly onUpdateOverenskommenKvantitet = (model: ForsorjningskallaAggregat, leverantorsorderradId, change) => {
        model.overenskommet = change.newValue;
        const command = new SetOverenskommenKvantitetLevorderCommand(i => {
            i.kundorderradId = model.id;
            i.leverantorsorderradId = leverantorsorderradId;
            i.kvantitet = change.newValue;
        });
        this.commandService.addTransactionCommand(command);
        change.complete();
    };

    private readonly onUpdateLeverantorsorderradKvantitet = (change: NumericCellChangeArg, model: LeverantorsorderradAggregat) => {
        model.overenskommet = change.newValue;
        this.commandService.addTransactionCommand(new SetLeverantorsorderradKvantitetCommand(c => {
            c.leverantorsorderradId = model.orderradId;
            c.overenskommenKvantitetLeveransrad = change.newValue;
        }));
        change.complete();
    };

    private readonly buildColumnDefinitions = (periods: IAggregatPeriod[]): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "",
            width: 30,
            readOnly: true,
            cellName: "settings"
        });

        columns.push({
            header: "Mottagningsplats",
            width: 180,
            readOnly: true,
            cellName: "rowHeader1"
        });

        columns.push({
            header: "Kund",
            width: 150,
            readOnly: true,
            cellName: "rowHeader2"
        });

        columns.push({
            header: "",
            width: 100,
            readOnly: true,
            cellName: "rowHeader3"
        });

        columns.push({
            header: "",
            width: 180,
            readOnly: true,
            cellName: "rowHeader4"
        });

        const periodCols = periods.map(period => ({
            header: period.name,
            width: 95,
            cellName: period.key,
            type: "numeric"
        }));

        columns.push(...periodCols);

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_OVERENSKOMMET"),
            cellName: "overenskommet",
            type: "numeric",
            width: 134
        });

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            cellName: "summa",
            type: "numeric",
            width: 78
        });
        return columns;
    };
}
